/**
 * Homepage Finder catalog: folders, preview URLs, and Cover Flow items.
 */

import { getFeaturedProjects } from '@/lib/projects';
import { SOCIAL_LINKS } from '@/lib/socialLinks';
import quotesData from '@/data/quotes.json';
import { prodengManifest } from '@/lib/prodengData';
import { postsFromManifest } from '@/lib/blogListing';
import { isUsableAlbumArtUrl } from '@/lib/musicSleeve';

export type FinderFolderId =
  | 'blog'
  | 'prodeng'
  | 'music'
  | 'projects'
  | 'uses'
  | 'about'
  | 'quotes'
  | 'connect';

export type MusicView = 'recents' | 'playlists' | 'liked';

export interface FinderFolder {
  id: FinderFolderId;
  label: string;
}

export interface FinderItem {
  id: string;
  title: string;
  subtitle?: string;
  href: string;
  previewUrl?: string;
  /** Prebuilt extruded mark from `one-square-mile logos --catalog connect`. */
  modelUrl?: string;
  color?: string;
  external?: boolean;
  nowPlaying?: boolean;
  kind: string;
}

export const FINDER_FOLDERS: readonly FinderFolder[] = [
  { id: 'blog', label: 'Blog' },
  { id: 'prodeng', label: 'ProdEng' },
  { id: 'music', label: 'Music' },
  { id: 'projects', label: 'Projects' },
  { id: 'uses', label: 'Uses' },
  { id: 'about', label: 'About' },
  { id: 'quotes', label: 'Quotes' },
  { id: 'connect', label: 'Connect' },
];

export function isFinderFolderId(value: unknown): value is FinderFolderId {
  return typeof value === 'string' && FINDER_FOLDERS.some((entry) => entry.id === value);
}

export function isMusicView(value: unknown): value is MusicView {
  return value === 'recents' || value === 'playlists' || value === 'liked';
}

/** Homepage Finder search: `/?folder=prodeng`, `/?folder=music&view=liked`. */
export type HomeSearch = {
  folder?: FinderFolderId;
  view?: MusicView;
};

export function parseHomeSearch(search: Record<string, unknown>): HomeSearch {
  const folder = isFinderFolderId(search.folder) ? search.folder : undefined;
  const view = folder === 'music' && isMusicView(search.view) && search.view !== 'recents' ? search.view : undefined;
  return { folder, view };
}

/** Omit defaults so `/` stays clean (`blog` + music `recents`). */
export function homeFinderSearch(folder: FinderFolderId, view?: MusicView): HomeSearch {
  return {
    folder: folder === 'blog' ? undefined : folder,
    view: folder === 'music' && view && view !== 'recents' ? view : undefined,
  };
}

export interface FinderFolderChrome {
  fill: string;
  hover: string;
  ink: string;
}

/** Finder Open button + sidebar highlight, matching each section’s page palette. */
export const FINDER_FOLDER_CHROME: Record<FinderFolderId, FinderFolderChrome> = {
  blog: { fill: '#0f766e', hover: '#0a524c', ink: '#ffffff' },
  prodeng: { fill: '#b45309', hover: '#92400e', ink: '#fffbeb' },
  music: { fill: '#1DB954', hover: '#159443', ink: '#ffffff' },
  projects: { fill: '#0891b2', hover: '#0e7490', ink: '#ffffff' },
  uses: { fill: '#c2410c', hover: '#9a3412', ink: '#fff7ed' },
  about: { fill: '#1e3a8a', hover: '#122560', ink: '#ffffff' },
  quotes: { fill: '#7e22ce', hover: '#5b1a96', ink: '#ffffff' },
  connect: { fill: '#5865F2', hover: '#3c45c3', ink: '#ffffff' },
};

export function hexAlpha(hex: string, alpha: number): string {
  const n = hex.replace('#', '');
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const VINYL_PREVIEW = '/finder-previews/vinyl.png';
/** Captured still of `/about?demo=journey` at progress 0 (FAST · GOOD · WELL). */
export const ABOUT_PREVIEW = '/finder-previews/about.png';
export const USES_PREVIEW = '/finder-previews/uses.png';

export function isMusicFinderKind(kind: string): boolean {
  return kind === 'scrobble' || kind === 'playlist' || kind === 'liked';
}

export function finderDocPreview(kind: 'blog' | 'prodeng', slug: string): string {
  return `/finder-previews/${kind}/${slug}.png`;
}

export function finderQuotePreview(index: number): string {
  return `/finder-previews/quotes/${index}.png`;
}

export function finderProjectPreview(id: string): string {
  return `/finder-previews/projects/${id}.png`;
}

export function finderConnectModel(id: string): string {
  return `/finder-previews/connect/${id}.glb`;
}

/** Coiny (OFL) for Connect Cover Flow labels. */
export const CONNECT_LABEL_FONT = '/finder-previews/connect/fonts/Coiny-Regular.ttf';
export const CONNECT_LABEL_FONT_FAMILY = '"Coiny", system-ui, sans-serif';

/** Matches ProdEng landing / article heroes (Georgia display). */
export const PRODENG_LABEL_FONT_FAMILY = '"Georgia", "Times New Roman", serif';
export const PRODENG_AMBER = '#b45309';

export interface BlogTeaserPost {
  slug: string;
  title: string;
  description?: string;
  date?: string;
  url: string;
  author?: string;
}

export function latestManifestPosts(limit = 8): BlogTeaserPost[] {
  return postsFromManifest().slice(0, limit);
}

export function blogFinderItems(posts?: BlogTeaserPost[]): FinderItem[] {
  const list = posts && posts.length > 0 ? posts : latestManifestPosts();
  return list.slice(0, 8).map((post) => ({
    id: post.slug,
    title: post.title,
    subtitle: post.author ?? 'Christian Battaglia',
    href: post.url,
    previewUrl: finderDocPreview('blog', post.slug),
    kind: 'post',
  }));
}

export function prodengFinderItems(): FinderItem[] {
  const articles = [...prodengManifest.articles]
    .filter((article) => article.published !== false)
    .sort((a, b) => a.articleNumber - b.articleNumber);

  const series: FinderItem = {
    id: 'series',
    title: prodengManifest.title,
    subtitle: prodengManifest.subtitle,
    href: '/prodeng',
    previewUrl: finderDocPreview('prodeng', 'series'),
    color: PRODENG_AMBER,
    kind: 'article',
  };

  return [
    series,
    ...articles.map((article) => ({
      id: article.slug,
      title: article.title,
      subtitle: `No. ${article.articleNumber} · ${article.readTime}`,
      href: article.url,
      previewUrl: finderDocPreview('prodeng', article.slug),
      color: PRODENG_AMBER,
      kind: 'article',
    })),
  ];
}

export function projectFinderItems(): FinderItem[] {
  return getFeaturedProjects().map((project) => ({
    id: project.id,
    title: project.title,
    subtitle: project.tags.slice(0, 3).join(' · '),
    href: '/projects',
    previewUrl: finderProjectPreview(project.id),
    kind: 'project',
  }));
}

export function usesFinderItems(): FinderItem[] {
  return [
    {
      id: 'desk',
      title: 'The desk',
      subtitle: 'Hardware, software, and daily tools',
      href: '/uses',
      previewUrl: USES_PREVIEW,
      kind: 'page',
    },
  ];
}

export function aboutFinderItems(): FinderItem[] {
  return [
    {
      id: 'journey',
      title: 'The journey',
      subtitle: 'Career fly-through',
      href: '/about',
      previewUrl: ABOUT_PREVIEW,
      kind: 'page',
    },
  ];
}

export function quoteFinderItems(): FinderItem[] {
  return quotesData.slice(0, 4).map((quote, index) => ({
    id: `quote-${index}`,
    title: quote.author,
    subtitle: quote.text.length > 80 ? `${quote.text.slice(0, 77).trim()}…` : quote.text,
    href: '/quotes',
    previewUrl: finderQuotePreview(index),
    kind: 'quote',
  }));
}

export function connectFinderItems(): FinderItem[] {
  return SOCIAL_LINKS.map((link) => ({
    id: link.id,
    title: link.label,
    subtitle: link.handle,
    href: link.href,
    modelUrl: finderConnectModel(link.id),
    color: link.color,
    external: true,
    kind: 'social',
  }));
}

export interface LastFmCoverTrack {
  name: string;
  artist: { '#text': string };
  image: Array<{ '#text': string; size: string }>;
  url?: string;
  '@attr'?: { nowplaying?: string };
}

export function lastFmCoverUrl(track: LastFmCoverTrack): string | undefined {
  const preferred = ['extralarge', 'large', 'medium'];
  for (const size of preferred) {
    const url = track.image.find((img) => img.size === size)?.['#text'];
    if (isUsableAlbumArtUrl(url)) return url;
  }
  const fallback = track.image.find((img) => img['#text'])?.['#text'];
  return isUsableAlbumArtUrl(fallback) ? fallback : undefined;
}

export function musicRecentsItems(tracks: LastFmCoverTrack[]): FinderItem[] {
  const items = tracks.slice(0, 10).map((track, index) => {
    const nowPlaying = track['@attr']?.nowplaying === 'true';
    return {
      id: `${track.name}-${track.artist['#text']}-${index}`,
      title: track.name,
      subtitle: track.artist['#text'],
      href: track.url || '/music',
      previewUrl: lastFmCoverUrl(track),
      external: Boolean(track.url),
      nowPlaying,
      kind: 'scrobble',
    };
  });

  if (items.length === 0) {
    return [
      {
        id: 'vinyl',
        title: 'Recent tracks',
        subtitle: 'Last.fm is quiet right now',
        href: '/music',
        kind: 'scrobble',
      },
    ];
  }

  const playingIndex = items.findIndex((item) => item.nowPlaying);
  if (playingIndex > 0) {
    const [playing] = items.splice(playingIndex, 1);
    items.unshift(playing);
  }
  return items;
}

export interface SpotifyPlaylistTeaser {
  spotifyId: string;
  name: string;
  image?: string;
  url: string;
}

export interface SpotifyLikedTeaser {
  spotifyId: string;
  name: string;
  artist: string;
  image?: string;
  url: string;
}

export function musicPlaylistItems(playlists: SpotifyPlaylistTeaser[]): FinderItem[] {
  if (playlists.length === 0) {
    return [
      {
        id: 'playlists-empty',
        title: 'Playlists',
        subtitle: 'Seed Spotify to fill this folder',
        href: '/music',
        kind: 'playlist',
      },
    ];
  }

  return playlists.slice(0, 12).map((playlist) => ({
    id: playlist.spotifyId,
    title: playlist.name,
    subtitle: 'Playlist',
    href: playlist.url,
    previewUrl: isUsableAlbumArtUrl(playlist.image) ? playlist.image : undefined,
    external: true,
    kind: 'playlist',
  }));
}

export function musicLikedItems(tracks: SpotifyLikedTeaser[]): FinderItem[] {
  if (tracks.length === 0) {
    return [
      {
        id: 'liked-empty',
        title: 'Liked songs',
        subtitle: 'Seed Spotify to fill this folder',
        href: '/music',
        kind: 'liked',
      },
    ];
  }

  return tracks.slice(0, 12).map((track) => ({
    id: track.spotifyId,
    title: track.name,
    subtitle: track.artist,
    href: track.url,
    previewUrl: isUsableAlbumArtUrl(track.image) ? track.image : undefined,
    external: true,
    kind: 'liked',
  }));
}
